
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

interface BlogAnalyticsData {
  totalViews: number;
  totalPosts: number;
  avgViewsPerPost: number;
  recentPostsCount: number;
  timeRange: string;
  topPosts: {
    id: string;
    title: string;
    slug: string;
    view_count: number;
    created_at: string;
  }[];
  recentPosts: {
    id: string;
    title: string;
    slug: string;
    view_count: number;
    created_at: string;
  }[];
  tagCounts: {
    id: string;
    name: string;
    category: string;
    count: number;
  }[];
  categoryCounts: {
    category: string;
    count: number;
  }[];
  postCreationByDate: {
    date: string;
    posts: number;
  }[];
  viewsByDateArray: {
    date: string;
    views: number;
  }[];
}

export function useBlogAnalytics(timeRange: '7days' | '30days' | '90days' = '7days') {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<BlogAnalyticsData | null>(null);

  useEffect(() => {
    const fetchBlogAnalytics = async () => {
      setLoading(true);
      setError(null);

      try {
        // Determine date range based on selection
        let startDate;
        switch (timeRange) {
          case '7days':
            startDate = subDays(new Date(), 7);
            break;
          case '30days':
            startDate = subDays(new Date(), 30);
            break;
          case '90days':
            startDate = subDays(new Date(), 90);
            break;
          default:
            startDate = subDays(new Date(), 7);
        }

        const startDateStr = startOfDay(startDate).toISOString();
        const endDateStr = endOfDay(new Date()).toISOString();

        // Get total views (sum of view_count across all blog posts)
        const { data: totalViewsData, error: totalViewsError } = await supabase
          .from('blog_posts')
          .select('view_count')
          .eq('status', 'published');
          
        if (totalViewsError) {
          console.error("Error fetching total views:", totalViewsError);
          throw totalViewsError;
        }
        
        const totalViews = totalViewsData.reduce((sum, post) => sum + (post.view_count || 0), 0);
        
        // Get the most viewed posts
        const { data: topPosts, error: topPostsError } = await supabase
          .from('blog_posts')
          .select('id, title, slug, view_count, created_at')
          .eq('status', 'published')
          .order('view_count', { ascending: false })
          .limit(10);
          
        if (topPostsError) {
          console.error("Error fetching top posts:", topPostsError);
          throw topPostsError;
        }
        
        // Get posts created within the time range to analyze recent performance
        const { data: recentPosts, error: recentPostsError } = await supabase
          .from('blog_posts')
          .select('id, title, slug, view_count, created_at')
          .eq('status', 'published')
          .gte('created_at', startDateStr)
          .lte('created_at', endDateStr)
          .order('created_at', { ascending: false });
          
        if (recentPostsError) {
          console.error("Error fetching recent posts:", recentPostsError);
          throw recentPostsError;
        }
        
        // Get tags with blog post count - Fixed query to avoid using blog_post_id as an embedded resource
        const { data: blogPostsTags, error: tagCountsError } = await supabase
          .from('blog_posts_tags')
          .select(`
            tag_id,
            blog_post_id
          `);
          
        if (tagCountsError) {
          console.error("Error fetching tag counts:", tagCountsError);
          throw tagCountsError;
        }

        // Get published posts to filter tag counts
        const { data: publishedPosts, error: publishedPostsError } = await supabase
          .from('blog_posts')
          .select('id')
          .eq('status', 'published');
          
        if (publishedPostsError) {
          console.error("Error fetching published posts:", publishedPostsError);
          throw publishedPostsError;
        }
        
        // Create a set of published post IDs for efficient filtering
        const publishedPostIds = new Set(publishedPosts.map(post => post.id));
        
        // Filter tag counts to only include published posts
        const filteredBlogPostsTags = blogPostsTags.filter(item => 
          publishedPostIds.has(item.blog_post_id)
        );

        // Fetch tag details
        const tagIds = [...new Set(filteredBlogPostsTags.map(item => item.tag_id))];
        const { data: tagDetails, error: tagDetailsError } = await supabase
          .from('tags')
          .select('id, name, category')
          .in('id', tagIds.length > 0 ? tagIds : ['00000000-0000-0000-0000-000000000000']);  // Provide a dummy UUID if tagIds is empty

        if (tagDetailsError) {
          console.error("Error fetching tag details:", tagDetailsError);
          throw tagDetailsError;
        }

        // Count occurrences of each tag
        const tagMap = new Map();
        filteredBlogPostsTags.forEach(item => {
          const count = tagMap.get(item.tag_id) || 0;
          tagMap.set(item.tag_id, count + 1);
        });

        // Transform tag counts data
        const transformedTagCounts = tagDetails?.map(tag => ({
          id: tag.id,
          name: tag.name,
          category: tag.category || 'Uncategorized',
          count: tagMap.get(tag.id) || 0
        })).sort((a, b) => b.count - a.count) || [];
        
        // Get popular categories (based on tag categories)
        const categoryCounts = [];
        const categoryMap = new Map();
        
        for (const tag of transformedTagCounts) {
          const category = tag.category || 'Uncategorized';
          const currentCount = categoryMap.get(category) || 0;
          categoryMap.set(category, currentCount + tag.count);
        }
        
        for (const [category, count] of categoryMap.entries()) {
          categoryCounts.push({ category, count });
        }
        
        categoryCounts.sort((a, b) => b.count - a.count);

        // Create date buckets for charts
        const postsByDate = {};
        const viewsByDate = {};
        
        // Initialize date buckets
        let currentDate = new Date(startDate);
        while (currentDate <= new Date()) {
          const dateKey = format(currentDate, 'yyyy-MM-dd');
          postsByDate[dateKey] = 0;
          viewsByDate[dateKey] = 0;
          
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        // Count posts by creation date
        for (const post of recentPosts) {
          const dateKey = format(new Date(post.created_at), 'yyyy-MM-dd');
          if (postsByDate[dateKey] !== undefined) {
            postsByDate[dateKey]++;
            viewsByDate[dateKey] += (post.view_count || 0);
          }
        }
        
        // Convert to array format for charts
        const postCreationByDate = Object.entries(postsByDate).map(([date, posts]) => ({
          date,
          posts: posts as number
        }));
        
        const viewsByDateArray = Object.entries(viewsByDate).map(([date, views]) => ({
          date,
          views: views as number
        }));
        
        // Calculate averages and totals
        const totalPosts = topPosts.length;
        const avgViewsPerPost = totalPosts > 0 ? Math.round(totalViews / totalPosts) : 0;
        const recentPostsCount = recentPosts.length;
        
        // Prepare analytics data
        const analyticsData: BlogAnalyticsData = {
          totalViews,
          totalPosts,
          avgViewsPerPost,
          recentPostsCount,
          timeRange,
          topPosts,
          recentPosts,
          tagCounts: transformedTagCounts,
          categoryCounts,
          postCreationByDate,
          viewsByDateArray
        };

        setData(analyticsData);
        
      } catch (err: any) {
        console.error('Error fetching blog analytics:', err);
        setError(err.message || 'An error occurred while fetching blog analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogAnalytics();
  }, [timeRange]);

  return { data, loading, error };
}
